import { Loader } from '@huggg/react-components';
import { Error } from 'components';
import {
  AllClaimedError,
  ExpiredError,
  RedeemedError,
  NotOwnerError,
  CancelledError,
} from 'components/Error/Custom';
import { withAuth } from 'hoc';
import { Layout } from 'layouts';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  fetchHuggg,
  fetchRedeemedProduct,
  getFetchError,
  getHuggg,
  getReceiver,
  getSender,
  getVouchers,
  isBlueHuggg,
  isExpired,
  isCancelled,
  isFetching,
  isFormRedeemed,
  isLoadingRedeemedProduct,
  isMultiRedeemable,
  isRedeemed,
  isStashed,
  isSuperhugggEmailClaim,
  isSuperhugggSMSClaim,
  isTestHuggg,
  isValid,
  isVoucherV2,
  shouldShowDetails,
  getMultiSegmentFlow,
  getGuidedChoiceConfig,
  getVoucherExpiry,
  hasVoucherToClaim,
  isFetchedHuggg,
  hasFulfilledVoucher,
  isOutOfStock,
  isVirginExperience,
  isVirginBrandProduct,
} from 'redux/modules/huggg';
import { isFirstRedeemed } from 'redux/modules/redeem';
import { isFirstStashed } from 'redux/modules/stash';
import {
  canRedeem,
  isPinRedeem,
  isVoucherRedeem,
  isValidRedemptionMethod,
} from 'utilities/redemption';

/**
 * Provide the wrapped component with a huggg
 *
 * 1. We have to set this variable to make sure that if we already have a huggg
 *    in redux state, we don't accidentally render child components. It prevents
 *    negative side-effects (double analytics tracking, missing huggg errors).
 */
export class WithHuggg extends Component {
  constructor() {
    super();

    this.forceFetching = false; /* [1] */
  }

  componentWillMount() {
    const { apiError, forceRefresh, isTestHuggg } = this.props;

    if (forceRefresh && !apiError && !isTestHuggg) {
      this.forceFetching = true;
    }
  }

  /**
   * In two instances we want to fetch a huggg, if we don't have one, or if
   * instructed to force fetch.
   */
  componentDidMount() {
    const {
      apiError,
      fetchHuggg,
      fetchRedeemedProduct,
      forceRefresh,
      isTestHuggg,
      shortcode,
    } = this.props;

    if ((forceRefresh || !this.exists()) && !apiError && !isTestHuggg) {
      this.forceFetching = false;
      fetchHuggg(shortcode);
    } else if (this.exists()) {
      // Make sure we have the redeemed product data when Huggg already exists from SSR
      fetchRedeemedProduct();
    }
  }

  exists() {
    const { huggg, isFetching } = this.props;

    return Boolean(!isFetching && huggg && huggg.id);
  }

  renderNotReady = () => {
    const { apiError, isFetching, layout } = this.props;
    const errorMessage =
      apiError &&
      (apiError.response ? apiError.response.data.error : apiError.message);

    return (
      <Layout layout={layout}>
        {apiError && (
          <Error
            errorMessage={errorMessage}
            data-test-id="withHuggg-api-error"
          />
        )}

        {isFetching && (
          <div
            style={{
              marginTop: '64px',
            }}
          >
            <Loader data-test-id="withHuggg-loader" />
          </div>
        )}
      </Layout>
    );
  };

  renderReady = () => {
    const {
      component: WrappedComponent,
      huggg,
      isAuthed,
      isBlueHuggg,
      isExpired,
      isCancelled,
      isFormRedeemed,
      isLoadingRedeemedProduct,
      isMultiRedeemable,
      isRedeemed,
      isStashed,
      isSuperhugggEmailClaim,
      isSuperhugggSMSClaim,
      isTestHuggg,
      isValid,
      isVoucherV2,
      forceRefresh,
      multiSegmentFlow,
      guidedChoiceConfig,
      receiver,
      userId,
      vouchers,
      voucherExpiryDate,
      layout,
      ...rest
    } = this.props;

    const isOwner = () => {
      if (isAuthed && isStashed) {
        return receiver && userId === receiver.id;
      }

      return true;
    };

    // Show loader while redeemed product is loading to avoid flash of RedeemedError
    if (isLoadingRedeemedProduct) {
      return (
        <Layout layout={layout}>
          <div style={{ marginTop: '64px' }}>
            <Loader data-test-id="withHuggg-redeemed-product-loader" />
          </div>
        </Layout>
      );
    }

    if (!isMultiRedeemable && isRedeemed && !this.props.isFirstRedeemed) {
      return (
        <Layout>
          <RedeemedError
            huggg={huggg}
            isBlueHuggg={isBlueHuggg}
            data-test-id="withHuggg-redeemed-error"
          />
        </Layout>
      );
    }

    if (!isTestHuggg && isExpired) {
      return (
        <Layout>
          <ExpiredError
            huggg={huggg}
            isBlueHuggg={isBlueHuggg}
            data-test-id="withHuggg-expired-error"
            voucherExpiryDate={voucherExpiryDate}
          />
        </Layout>
      );
    }

    if (!isTestHuggg && isCancelled) {
      return (
        <Layout>
          <CancelledError
            huggg={huggg}
            isBlueHuggg={isBlueHuggg}
            data-test-id="withHuggg-cancelled-error"
          />
        </Layout>
      );
    }

    if (!isTestHuggg && huggg.isSuperhuggg && !isValid) {
      return (
        <Layout>
          <AllClaimedError
            huggg={huggg}
            isBlueHuggg={isBlueHuggg}
            data-test-id="withHuggg-expired-error"
          />
        </Layout>
      );
    }

    if (!isOwner()) {
      return (
        <Layout>
          <NotOwnerError
            huggg={huggg}
            isBlueHuggg={isBlueHuggg}
            data-test-id="withHuggg-not-owner-error"
          />
        </Layout>
      );
    }

    return (
      <WrappedComponent
        huggg={huggg}
        isAuthed={isAuthed}
        isBlueHuggg={isBlueHuggg}
        isFormRedeemed={isFormRedeemed}
        isRedeemed={isRedeemed}
        isStashed={isStashed}
        isSuperhugggEmailClaim={isSuperhugggEmailClaim}
        isSuperhugggSMSClaim={isSuperhugggSMSClaim}
        isTestHuggg={isTestHuggg}
        isVoucherV2={isVoucherV2}
        isMultiRedeemable={isMultiRedeemable}
        vouchers={vouchers}
        multiSegmentFlow={multiSegmentFlow}
        guidedChoiceConfig={guidedChoiceConfig}
        {...rest}
      />
    );
  };

  render() {
    return !this.forceFetching && this.exists()
      ? this.renderReady()
      : this.renderNotReady();
  }
}

WithHuggg.propTypes = {
  fetchHuggg: PropTypes.func.isRequired,
  fetchRedeemedProduct: PropTypes.func.isRequired,
  forceRefresh: PropTypes.bool.isRequired,
  huggg: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  shortcode: PropTypes.string.isRequired,
  isAuthed: PropTypes.bool.isRequired,
  isBlueHuggg: PropTypes.bool.isRequired,
  isFirstRedeemed: PropTypes.bool.isRequired,
  isFormRedeemed: PropTypes.bool.isRequired,
  isLoadingRedeemedProduct: PropTypes.bool.isRequired,
  isMultiRedeemable: PropTypes.bool,
  isRedeemed: PropTypes.bool.isRequired,
  isStashed: PropTypes.bool.isRequired,
  isFetchedHuggg: PropTypes.bool.isRequired,
  isSuperhugggEmailClaim: PropTypes.bool.isRequired,
  isSuperhugggSMSClaim: PropTypes.bool.isRequired,
  isTestHuggg: PropTypes.bool.isRequired,
  apiError: PropTypes.object,
  component: PropTypes.any,
  isExpired: PropTypes.bool,
  isCancelled: PropTypes.bool,
  isValid: PropTypes.bool,
  isVoucherV2: PropTypes.bool,
  receiver: PropTypes.object,
  userId: PropTypes.string,
  vouchers: PropTypes.array,
  multiSegmentFlow: PropTypes.string,
  guidedChoiceConfig: PropTypes.array,
  voucherExpiryDate: PropTypes.string,
  layout: PropTypes.string,
};

export default (WrappedComponent, options) => {
  const settings = {
    forceRefresh: { huggg: false },
    isReveal: false,
    ...options,
  };

  const mapStateToProps = (state, ownProps) => {
    const huggg = getHuggg(state);
    const shortcode = ownProps.match.params.shortcode;

    const validRedemptionMethod = huggg.id
      ? isValidRedemptionMethod(huggg)
      : false;

    return {
      apiError: getFetchError(state),
      component: WrappedComponent,
      forceRefresh: settings.forceRefresh.huggg,
      isBlueHuggg: isBlueHuggg(state),
      isRedeemable: canRedeem(huggg),
      isPinRedeem: isPinRedeem(huggg),
      isVoucherRedeem: isVoucherRedeem(huggg),
      isRedeemed: isRedeemed(state),
      hasFulfilledVoucher: hasFulfilledVoucher(state),
      isOutOfStock: isOutOfStock(state),
      isStashed: isStashed(state),
      isFirstStashed: isFirstStashed(state),
      isFirstRedeemed: isFirstRedeemed(state),
      isFormRedeemed: isFormRedeemed(state),
      isExpired: isExpired(state),
      isCancelled: isCancelled(state),
      isFetching: isFetching(state),
      isLoadingRedeemedProduct: isLoadingRedeemedProduct(state),
      isMultiRedeemable: isMultiRedeemable(state),
      isValid: isValid(state),
      isValidRedemptionMethod: validRedemptionMethod,
      isVoucherV2: isVoucherV2(state),
      isTestHuggg: isTestHuggg(state),
      isSuperhugggEmailClaim: isSuperhugggEmailClaim(state),
      isSuperhugggSMSClaim: isSuperhugggSMSClaim(state),
      isVirginExperience: isVirginExperience(state),
      isVirginBrandProduct: isVirginBrandProduct(state),
      receiver: getReceiver(state),
      sender: getSender(state),
      showDetails: shouldShowDetails(state),
      vouchers: getVouchers(state),
      multiSegmentFlow: getMultiSegmentFlow(state),
      guidedChoiceConfig: getGuidedChoiceConfig(state),
      voucherExpiryDate: getVoucherExpiry(state),
      hasVoucherToClaim: hasVoucherToClaim(state),
      isFetchedHuggg: isFetchedHuggg(state),
      huggg,
      shortcode,
      layout: settings.isReveal ? 'reveal' : 'default',
    };
  };

  return compose(
    withAuth,
    withRouter,
    connect(mapStateToProps, { fetchHuggg, fetchRedeemedProduct }),
  )(WithHuggg);
};
